import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router';

import { canUseInswitchForCompany } from '@/shared/inswitch/utils/inswitch.util';
import { companyService } from '@/shared/common';
import { useRemoteConfigStore } from '@/shared/firebase';
import { resolveRouteFeatureFlagAllowed } from '@/shared/firebase/utils/resolveRouteFeatureFlagAllowed.util';
import { useRoutesStore } from '@/stores/routes';
import { useWalletStore } from '@/stores/wallet';
import { hasUserEnabledPaymentLinksWallet } from '@/shared/inswitch/utils/paymentLinksWallet.util';

/**
 * Impide el acceso a links de cobro si:
 * - El feature flag `feature_inswitch_payment_links` no habilita a la empresa.
 * - El país o la actividad de la empresa están restringidos para InSwitch.
 * - Backend deshabilitó el feature (ningún método InSwitch tiene thirdPartyPayment === 'BOTH').
 */
export async function validatePaymentLinksAvailability(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized
): Promise<boolean | RouteLocationRaw> {
  if (!to.path.toLowerCase().startsWith('/payment-links')) {
    return true;
  }

  const remoteConfigStore = useRemoteConfigStore();
  if (!resolveRouteFeatureFlagAllowed(remoteConfigStore, 'feature_inswitch_payment_links')) {
    return { name: 'movements' };
  }

  const result = await companyService.getCompanyInformation();

  if (result.isLeft()) {
    return { name: 'movements' };
  }

  const info = result.value.data;
  if (!canUseInswitchForCompany(info.country ?? '', info.activityId)) {
    return { name: 'movements' };
  }

  const routeStore = useRoutesStore();
  if (!routeStore.routes.length) {
    try {
      await routeStore.setRoutes();
    } catch {
      return { name: 'movements' };
    }
  }

  const walletStore = useWalletStore();
  if (!walletStore.wallets.length) {
    await walletStore.setWallets();
  }

  if (!hasUserEnabledPaymentLinksWallet(routeStore.routes, walletStore.wallets, info.country ?? '')) {
    return { name: 'movements' };
  }

  return true;
}
